import { useEffect, useState } from "react";
import { Oval } from "react-loader-spinner";
import { getRealFileName } from "src/Common/common";
import { GhostButton, ModalShell, OutlineButton, PrimaryButton } from "../upload/uploadModalUI";

// ---------------------------------------------------------------- helpers

export const formatMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `$${numeric.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const getDocumentDisplayName = (document) =>
  document?.documentName || getRealFileName(document?.fileName) || document?.fileName || "";

/**
 * Rebuild a scan result ({ status, best, candidates }) from the fields stored
 * on a scanned-document row, mirroring the backend's decide() logic. Lets the list
 * re-open review without re-scanning.
 */
export const deriveScanResult = (document) => {
  const candidates = document?.scanCandidates || [];
  if (!candidates.length) return { status: "NOT_FOUND", best: null, candidates: [] };
  const confidentTotals = candidates.filter((c) => c.kind === "total" && c.score >= 85);
  if (confidentTotals.length === 1) {
    return { status: "FOUND", best: confidentTotals[0], candidates };
  }
  return { status: "MULTIPLE", best: candidates[0], candidates };
};

const candidateDescription = (candidate, isBest) => {
  if (isBest) return "Likely Total";
  if (candidate.kind === "subtotal") return "Subtotal amount detected";
  if (candidate.kind === "tax") return "Tax amount detected";
  return "Amount detected";
};

const ViewFileButton = ({ url }) => (
  <button
    type="button"
    onClick={() => window.open(url, "_blank")}
    className="mr-auto h-[40px] px-1 rounded-[6px] flex items-center gap-2 font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#7C829A] cursor-pointer"
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8.00031 2.83203C9.51849 2.83209 11.0023 3.28319 12.264 4.1276C13.5257 4.97208 14.5087 6.17193 15.0875 7.57552C15.0898 7.58103 15.092 7.58687 15.0941 7.59245C15.1912 7.85435 15.1913 8.14241 15.0941 8.4043C15.092 8.40987 15.0898 8.41572 15.0875 8.42122C14.5086 9.82475 13.5257 11.0247 12.264 11.8691C11.0023 12.7135 9.51846 13.1647 8.00031 13.1647C6.48204 13.1647 4.99775 12.7136 3.73598 11.8691C2.47426 11.0247 1.49132 9.82475 0.912415 8.42122C0.910145 8.41572 0.907976 8.40988 0.905904 8.4043C0.808691 8.14241 0.808727 7.85434 0.905904 7.59245L0.912415 7.57552C1.49131 6.17193 2.47422 4.97209 3.73598 4.1276C4.99776 3.28313 6.482 2.83203 8.00031 2.83203ZM8.00031 3.83203C6.68004 3.83203 5.38917 4.22401 4.29197 4.95833C3.19838 5.69026 2.34548 6.7293 1.84145 7.94466C1.82965 7.97948 1.83022 8.01729 1.8421 8.05208C2.34615 9.26728 3.19849 10.3066 4.29197 11.0384C5.38916 11.7727 6.68008 12.1647 8.00031 12.1647C9.32042 12.1647 10.6109 11.7726 11.708 11.0384C12.8014 10.3066 13.6531 9.26718 14.1572 8.05208C14.1692 8.01716 14.1698 7.9796 14.1579 7.94466C13.6538 6.7294 12.8015 5.69021 11.708 4.95833C10.6109 4.22407 9.32045 3.83209 8.00031 3.83203Z" />
      <path d="M9.49999 7.99829C9.49999 7.16986 8.82842 6.49829 7.99999 6.49829C7.17156 6.49829 6.49999 7.16986 6.49999 7.99829C6.49999 8.82671 7.17156 9.49829 7.99999 9.49829C8.82842 9.49829 9.49999 8.82671 9.49999 7.99829ZM10.5 7.99829C10.5 9.379 9.3807 10.4983 7.99999 10.4983C6.61928 10.4983 5.49999 9.379 5.49999 7.99829C5.49999 6.61758 6.61928 5.49829 7.99999 5.49829C9.3807 5.49829 10.5 6.61758 10.5 7.99829Z" />
    </svg>
    View PDF
  </button>
);

// ---------------------------------------------------------- scanning modal

/**
 * "Scanning..." state shown while the scan endpoint runs. The backend call is
 * a single request, so the "Reading document" -> "Finding total" step
 * hand-off is timed locally for feedback.
 */
export const DocumentScanningModal = ({ noun = "Document", fileLabel, onCancel }) => {
  const [step, setStep] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setStep(2), 2500);
    return () => clearTimeout(timer);
  }, []);

  const StepRow = ({ state, doneLabel, activeLabel }) => (
    <div className="flex items-center gap-3">
      {state === "done" ? (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
          <circle cx="9" cy="9" r="9" fill="#22C55E" />
          <path d="M5.2 9.4L7.8 12L12.8 6.6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : state === "active" ? (
        <span className="w-[18px] h-[18px] shrink-0 flex items-center justify-center">
          <Oval height="16" width="16" color="#0065FD" strokeWidth={6} secondaryColor="#DCE9FF" visible />
        </span>
      ) : (
        <span className="w-[18px] h-[18px] shrink-0 rounded-full border-2 border-[#E5E8F3]" />
      )}
      <span
        className={
          state === "active"
            ? "font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
            : state === "done"
              ? "font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
              : "font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]"
        }
      >
        {state === "active" ? activeLabel : doneLabel}
      </span>
    </div>
  );

  return (
    <ModalShell
      title={`Upload ${noun}`}
      width="w-[500px]"
      headerDimmed
      onClose={onCancel}
      footer={
        <>
          <GhostButton disabled>Cancel</GhostButton>
          <PrimaryButton loading loadingLabel="Scanning...">
            Scanning...
          </PrimaryButton>
        </>
      }
    >
      <div className="p-8 flex flex-col items-center gap-6">
        <div className="relative w-[120px] h-[120px]">
          <div className="absolute inset-0 rounded-full border-[6px] border-[#EEF0F9] border-t-[#0065FD] animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="#0065FD">
              <path d="M26 13C26 15.8687 25.0688 18.5188 23.5 20.6688L31.4125 28.5875C32.1937 29.3687 32.1937 30.6375 31.4125 31.4188C30.6313 32.2 29.3625 32.2 28.5812 31.4188L20.6688 23.5C18.5188 25.075 15.8687 26 13 26C5.81875 26 0 20.1812 0 13C0 5.81875 5.81875 0 13 0C20.1812 0 26 5.81875 26 13ZM13 22C14.1819 22 15.3522 21.7672 16.4442 21.3149C17.5361 20.8626 18.5282 20.1997 19.364 19.364C20.1997 18.5282 20.8626 17.5361 21.3149 16.4442C21.7672 15.3522 22 14.1819 22 13C22 11.8181 21.7672 10.6478 21.3149 9.55585C20.8626 8.46392 20.1997 7.47177 19.364 6.63604C18.5282 5.80031 17.5361 5.13738 16.4442 4.68508C15.3522 4.23279 14.1819 4 13 4C11.8181 4 10.6478 4.23279 9.55585 4.68508C8.46392 5.13738 7.47177 5.80031 6.63604 6.63604C5.80031 7.47177 5.13738 8.46392 4.68508 9.55585C4.23279 10.6478 4 11.8181 4 13C4 14.1819 4.23279 15.3522 4.68508 16.4442C5.13738 17.5361 5.80031 18.5282 6.63604 19.364C7.47177 20.1997 8.46392 20.8626 9.55585 21.3149C10.6478 21.7672 11.8181 22 13 22Z" />
            </svg>
          </div>
        </div>
        {fileLabel && (
          <span
            className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] truncate max-w-full"
            title={fileLabel}
          >
            {fileLabel}
          </span>
        )}
        <div className="self-stretch pt-4 flex flex-col gap-4">
          <StepRow state="done" doneLabel="File uploaded" />
          <StepRow state={step === 1 ? "active" : "done"} doneLabel="Document read" activeLabel="Reading document..." />
          <StepRow state={step === 1 ? "pending" : "active"} doneLabel={`Finding ${noun.toLowerCase()} total`} activeLabel={`Finding ${noun.toLowerCase()} total`} />
        </div>
      </div>
    </ModalShell>
  );
};

// ------------------------------------------------------------ result modal

/**
 * Post-scan review. Renders the right state for a scan result:
 *   FOUND     -> green "Extracted Total" card (screenshot 2)
 *   MULTIPLE  -> radio candidate list      (screenshot 5)
 *   NOT_FOUND -> failure + manual entry    (screenshot 6)
 * plus an inline "enter/edit amount" sub-state shared by all three.
 *
 * onConfirm(amount, sourcePage, sourceLabel) persists; onClose leaves the
 * document NEEDS_REVIEW; onRescan (optional) re-runs the scan.
 */
export const DocumentReviewModal = ({
  document,
  noun,
  totalLabel,
  getFileUrl,
  result,
  busy,
  initialMode,
  onConfirm,
  onClose,
  onRescan,
}) => {
  const { status, best, candidates } = result;
  const [mode, setMode] = useState(initialMode || "result");
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Editing an already-reviewed document starts from its stored amount.
  const [customAmount, setCustomAmount] = useState(
    initialMode === "edit" && document?.extractedAmount ? String(Number(document.extractedAmount)) : "",
  );

  const displayName = getDocumentDisplayName(document);

  // ---- manual / edit amount ----
  if (mode === "edit") {
    const numeric = Number(String(customAmount).replace(/[^0-9.]/g, ""));
    const valid = Number.isFinite(numeric) && numeric > 0;
    return (
      <ModalShell
        title={`Enter ${noun} Amount`}
        width="w-[500px]"
        onClose={onClose}
        footer={
          <>
            <GhostButton
              onClick={() => (initialMode === "edit" ? onClose() : setMode("result"))}
              disabled={busy}
            >
              {initialMode === "edit" ? "Cancel" : "Back"}
            </GhostButton>
            <PrimaryButton
              onClick={() => valid && onConfirm(numeric, null, "Manual entry")}
              disabled={!valid}
              loading={busy}
              loadingLabel="Saving..."
            >
              Confirm Amount
            </PrimaryButton>
          </>
        }
      >
        <div className="p-6 flex flex-col gap-1">
          <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
            {noun} Amount
          </span>
          <div className="h-[40px] flex items-center gap-2 px-3 rounded-[6px] border border-[#E5E8F3] focus-within:border-[#0065FD]">
            <span className="font-OpenSansSemiBold text-[14px] text-[#7C829A]">$</span>
            <input
              autoFocus
              type="text"
              inputMode="decimal"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="0.00"
              className="flex-1 outline-none font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
            />
          </div>
          <span className="mt-1 font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
            {displayName}
          </span>
        </div>
      </ModalShell>
    );
  }

  // ---- not found ----
  if (status === "NOT_FOUND") {
    return (
      <ModalShell title={`${noun} Total Not Found`} width="w-[500px]" onClose={onClose}>
        <div className="p-8 flex flex-col items-center gap-6">
          <div className="w-[64px] h-[64px] rounded-full bg-[#FEF2F2] flex items-center justify-center">
            <svg width="32" height="28" viewBox="0 0 32 28" fill="#EF4444">
              <path d="M0 3.5C0 1.56953 1.56953 0 3.5 0H12.25V7C12.25 7.96797 13.032 8.75 14 8.75H21V10.8609C16.9586 12.0039 14 15.7172 14 20.125C14 23.357 15.5914 26.2117 18.0305 27.9617C17.8555 27.9891 17.6805 28 17.5 28H3.5C1.56953 28 0 26.4305 0 24.5V3.5ZM21 7H14V0L21 7ZM23.625 12.25C25.7136 12.25 27.7166 13.0797 29.1935 14.5565C30.6703 16.0334 31.5 18.0364 31.5 20.125C31.5 22.2136 30.6703 24.2166 29.1935 25.6935C27.7166 27.1703 25.7136 28 23.625 28C21.5364 28 19.5334 27.1703 18.0565 25.6935C16.5797 24.2166 15.75 22.2136 15.75 20.125C15.75 18.0364 16.5797 16.0334 18.0565 14.5565C19.5334 13.0797 21.5364 12.25 23.625 12.25ZM26.868 18.118C27.207 17.7789 27.207 17.2211 26.868 16.882C26.5289 16.543 25.9711 16.543 25.632 16.882L23.625 18.8891L21.618 16.882C21.2789 16.543 20.7211 16.543 20.382 16.882C20.043 17.2211 20.043 17.7789 20.382 18.118L22.3891 20.125L20.382 22.132C20.043 22.4711 20.043 23.0289 20.382 23.368C20.7211 23.707 21.2789 23.707 21.618 23.368L23.625 21.3609L25.632 23.368C25.9711 23.707 26.5289 23.707 26.868 23.368C27.207 23.0289 27.207 22.4711 26.868 22.132L24.8609 20.125L26.868 18.118Z" />
            </svg>
          </div>
          <div className="flex flex-col items-center gap-2">
            <h3 className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] text-center">
              We Couldn't Reliably Extract The Total
            </h3>
            <p className="max-w-[420px] font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A] text-center">
              This can happen if the PDF formatting is complex or if the total is not clearly labeled. You can try
              scanning again or enter the amount manually.
            </p>
          </div>
          <div className="w-full pt-4 flex flex-col gap-3">
            <PrimaryButton onClick={() => setMode("edit")} disabled={busy}>
              Enter Amount Manually
            </PrimaryButton>
            {onRescan && (
              <OutlineButton onClick={onRescan} disabled={busy}>
                Try Scanning Again
              </OutlineButton>
            )}
            <GhostButton onClick={onClose}>Cancel</GhostButton>
          </div>
        </div>
      </ModalShell>
    );
  }

  // ---- multiple candidates ----
  if (status === "MULTIPLE") {
    const selected = candidates[selectedIndex];
    return (
      <ModalShell
        title={`${noun} Total Found`}
        width="w-[500px]"
        onClose={onClose}
        footer={
          <>
            <ViewFileButton url={getFileUrl(document)} />
            <OutlineButton onClick={() => setMode("edit")} disabled={busy}>
              Enter Custom
            </OutlineButton>
            <PrimaryButton
              onClick={() => selected && onConfirm(Number(selected.amount), selected.page, selected.label)}
              disabled={!selected}
              loading={busy}
              loadingLabel="Saving..."
            >
              Confirm Selected
            </PrimaryButton>
          </>
        }
      >
        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-start gap-2 p-3 rounded-[8px] bg-[#FEFCE8] outline outline-1 -outline-offset-1 outline-[#FEF08A]">
            <svg width="18" height="16" viewBox="0 0 18 16" fill="#EAB308" className="shrink-0 mt-[1px]">
              <path d="M17.2508 12.8196L10.4188 0.954739C10.2481 0.664055 10.0044 0.423035 9.71178 0.255567C9.41921 0.0880999 9.08795 0 8.75084 0C8.41373 0 8.08247 0.0880999 7.7899 0.255567C7.49732 0.423035 7.25359 0.664055 7.08287 0.954739L0.250838 12.8196C0.0865693 13.1007 0 13.4205 0 13.7461C0 14.0718 0.0865693 14.3915 0.250838 14.6727C0.419377 14.9651 0.66269 15.2075 0.955808 15.3748C1.24893 15.5422 1.58129 15.6286 1.91881 15.6251H15.5829C15.9201 15.6283 16.2522 15.5418 16.545 15.3744C16.8378 15.2071 17.0809 14.9649 17.2493 14.6727C17.4138 14.3917 17.5006 14.072 17.5009 13.7464C17.5012 13.4207 17.4149 13.1009 17.2508 12.8196ZM8.12584 6.25005C8.12584 6.08429 8.19169 5.92532 8.3089 5.80811C8.42611 5.6909 8.58508 5.62505 8.75084 5.62505C8.9166 5.62505 9.07557 5.6909 9.19278 5.80811C9.30999 5.92532 9.37584 6.08429 9.37584 6.25005V9.37505C9.37584 9.54081 9.30999 9.69978 9.19278 9.81699C9.07557 9.9342 8.9166 10.0001 8.75084 10.0001C8.58508 10.0001 8.42611 9.9342 8.3089 9.81699C8.19169 9.69978 8.12584 9.54081 8.12584 9.37505V6.25005ZM8.75084 13.1251C8.56542 13.1251 8.38416 13.0701 8.22999 12.9671C8.07582 12.864 7.95566 12.7176 7.8847 12.5463C7.81374 12.375 7.79518 12.1865 7.83135 12.0047C7.86753 11.8228 7.95681 11.6558 8.08792 11.5246C8.21904 11.3935 8.38608 11.3042 8.56794 11.2681C8.7498 11.2319 8.9383 11.2505 9.1096 11.3214C9.28091 11.3924 9.42733 11.5125 9.53034 11.6667C9.63335 11.8209 9.68834 12.0021 9.68834 12.1876C9.68834 12.4362 9.58957 12.6746 9.41375 12.8505C9.23794 13.0263 8.99948 13.1251 8.75084 13.1251Z" />
            </svg>
            <span className="flex-1 font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
              Multiple potential totals were found. Please select the correct amount to proceed.
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {candidates.map((candidate, index) => {
              const isSelected = index === selectedIndex;
              // Value comparison — `best` and the list entry are separate
              // objects after the API round-trip.
              const isBest =
                !!best &&
                candidate.kind === "total" &&
                candidate.amount === best.amount &&
                candidate.kind === best.kind;
              return (
                <button
                  key={`${candidate.amount}-${candidate.kind}`}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  className={`w-full flex items-center gap-3 p-4 rounded-[12px] text-left cursor-pointer ${
                    isSelected ? "bg-[#EAF3FF] outline outline-1 -outline-offset-1 outline-[#0065FD]" : "outline outline-1 -outline-offset-1 outline-[#E5E8F3]"
                  }`}
                >
                  <span
                    className={`w-[16px] h-[16px] shrink-0 rounded-full flex items-center justify-center ${
                      isSelected ? "bg-[#0065FD]" : "bg-white border border-[#C9CEE2]"
                    }`}
                  >
                    {isSelected && <span className="w-[7px] h-[7px] rounded-full bg-white" />}
                  </span>
                  <span className="flex-1 min-w-0 flex flex-col">
                    <span className="font-OpenSansSemiBold text-[16px] leading-[24px] text-[#1A2332]">
                      {formatMoney(candidate.amount)}
                    </span>
                    <span
                      className={
                        isBest
                          ? "font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#0065FD]"
                          : "font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]"
                      }
                    >
                      {candidateDescription(candidate, isBest)}
                    </span>
                  </span>
                  <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] shrink-0">
                    Page {candidate.page}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </ModalShell>
    );
  }

  // ---- single confident total ----
  return (
    <ModalShell
      title={`${noun} Total Found`}
      width="w-[500px]"
      onClose={onClose}
      footer={
        <>
          <ViewFileButton url={getFileUrl(document)} />
          <OutlineButton onClick={() => setMode("edit")} disabled={busy}>
            Edit Amount
          </OutlineButton>
          <PrimaryButton
            onClick={() => onConfirm(Number(best.amount), best.page, best.label)}
            loading={busy}
            loadingLabel="Saving..."
          >
            Confirm Amount
          </PrimaryButton>
        </>
      }
    >
      <div className="p-6 flex flex-col gap-6">
        <div className="flex flex-col items-center gap-2 p-6 rounded-[12px] bg-[#F0FDF4] outline outline-1 -outline-offset-1 outline-[#BBF7D0]">
          <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#15803D] uppercase">
            Extracted Total
          </span>
          <span className="font-OpenSansSemiBold text-[32px] leading-[40px] text-[#1A2332]">
            {formatMoney(best.amount)}
          </span>
          {best.page != null && (
            <span className="pt-1 flex items-center gap-[6px] font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
              <svg width="9" height="11" viewBox="0 0 9 11" fill="none" className="shrink-0">
                <path
                  d="M0.634 10C0.721554 10.1516 0.847394 10.2776 0.998932 10.3654C1.15047 10.4531 1.32239 10.4996 1.4975 10.5H7.5C7.76522 10.5 8.01957 10.3946 8.20711 10.2071C8.39464 10.0196 8.5 9.76522 8.5 9.5V3L6 0.5H1.5C1.23478 0.5 0.98043 0.605357 0.792893 0.792893C0.605357 0.98043 0.5 1.23478 0.5 1.5V3"
                  stroke="#7C829A"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Source: Page {best.page}
              {best.label ? ` — ${best.label}` : ""}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] uppercase">
            File Name
          </span>
          <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] truncate" title={displayName}>
            {displayName}
          </span>
        </div>

        <div className="flex items-start gap-[6px]">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#868DA6" strokeWidth="2" className="shrink-0 mt-[3px]">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4m0-4h.01" strokeLinecap="round" />
          </svg>
          <span className="flex-1 font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6]">
            Once confirmed, this amount will be included in the {totalLabel}.
          </span>
        </div>
      </div>
    </ModalShell>
  );
};
